import { useAction, useConvexAuth, useMutation } from 'convex/react';
import { useQuery } from '@tanstack/react-query';
import { convexQuery } from '@convex-dev/react-query';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { CLIENT_REFRESH_THRESHOLD_MS } from '@/convex/media/url_freshness';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMessageReadScope } from '@/contexts/message-read-scope-context';

// Cache storage URLs across mounts to avoid duplicate queries.
// For private objects we cache the signed URL together with its expiry so we can
// reuse it until it is near-expiry, then refresh.
type CachedEntry = {
  url: string | null;
  urlExpiresAt: number | null;
};
const storageUrlCache: Map<string, CachedEntry> = new Map();

// Legacy Convex `_storage` ids (no '/', unlike R2 storage keys) resolve through
// the historical `assets.queries.getStorageUrl` path, which understands
// `ctx.storage` ids. Cached separately with the old null-caching semantics.
const legacyStorageIdUrlCache: Map<string, string | null> = new Map();

// Client refresh threshold: refresh when a signed URL has under 2 minutes left.
// Imported, not mirrored — `convex/media/url_freshness` is dependency-free
// precisely so the client and the resolver cannot drift. The server side of the
// handshake is `REFRESH_MINT_THRESHOLD_MS`, which is >= this value, so a refresh
// asked for at this threshold always comes back genuinely re-signed rather than
// as the same near-expiry URL.

// In-flight refresh guard, shared across ALL hook instances so two components
// rendering the same key don't both call the refresh mutation. The loser does
// NOT silently give up: it reports `deferred`, and its caller re-arms a short
// timer that picks up whatever the winner wrote to the cache.
const refreshingKeys = new Set<string>();

// Delay before retrying a failed refresh (the mutation is idempotent).
const REFRESH_RETRY_DELAY_MS = 5_000;

// Floor for the proactive refresh timer. A server that somehow returns a URL
// already inside the refresh threshold would otherwise make the timer fire
// back-to-back; this turns that into a slow poll instead of a spin. It is also
// the delay a component waits when another component holds the in-flight slot
// for the same key.
const MIN_PROACTIVE_REFRESH_DELAY_MS = 5_000;

// Back-off used when a refresh completed but the expiry did NOT move forward.
// With the current thresholds the server always re-signs at the point the
// client asks, so this should not happen — but if it ever does (clock skew, a
// deployment mid-rollout with a narrower mint boundary), the timer must still
// re-arm rather than dying, and it must not turn into a 5s poll against a
// server that has nothing new to give.
const NON_ADVANCING_REFRESH_BACKOFF_MS = 30_000;

/**
 * What one refresh attempt actually achieved. The caller needs the distinction:
 * `refreshed`/`observed` both carry a usable entry to render and to arm the next
 * timer against, while `deferred`/`failed` carry none and mean "come back soon".
 */
type RefreshOutcome =
  | { kind: 'refreshed'; entry: CachedEntry }
  | { kind: 'observed'; entry: CachedEntry }
  | { kind: 'deferred' }
  | { kind: 'failed' };

function cachedEntryIsFresh(entry: CachedEntry | undefined): boolean {
  if (!entry || entry.url == null) return false;
  if (entry.urlExpiresAt == null) return true; // public URL, no expiry
  return entry.urlExpiresAt - Date.now() > CLIENT_REFRESH_THRESHOLD_MS;
}

// Anonymous share-scope cache, keyed by `${token}|${storageKey}` so URLs
// resolved through one share token can never bleed into another scope (or
// into the member cache above, which is keyed by storage key alone).
const anonymousShareUrlCache: Map<string, string | null> = new Map();

// One in-flight public-copy trigger per workspaceFileId across all hook
// instances (the action is idempotent server-side; this just avoids spam).
const pendingPublicCopyTriggers = new Set<string>();

export type StorageUrlState = {
  url: string | null;
  status: 'loading' | 'ready' | 'unavailable';
};

/**
 * Hook to convert a storage reference (storageKey, legacy storageId, or an
 * already-resolved URL) to a displayable URL.
 *
 * Behavior (resolver-always for R2 storage keys):
 * - Already-a-URL or data: URI → returned as-is.
 * - Legacy Convex `_storage` id (no '/') → resolved via the historical
 *   `assets.queries.getStorageUrl` path (unchanged).
 * - Any other storage-key reference → resolved via the authenticated narrow
 *   resolver (`api.storage.resolver.resolveStorageUrl`). The resolver returns a
 *   signed URL for PRIVATE objects and a derived public URL for public /
 *   unregistered ones, so correctness is identical to the old client-derive
 *   fast path for public keys while never leaking a broken public CDN URL for a
 *   private key. If the resolver reports `refreshRequired` (private URL
 *   missing/expired/near-expiry), the hook calls the idempotent
 *   `refreshStorageUrl` mutation to mint a fresh signed URL.
 *
 * There is intentionally NO client-side public-URL derivation: a private
 * object's key has the exact same `orgSlug/mediaType/uuid.ext` shape as a public
 * one, so the client cannot tell them apart and must never guess. Cost is one
 * cached Convex query per distinct key (the module-level caches below dedupe
 * across mounts and across the session).
 */
export function useStorageUrlState(
  storageIdOrUrl: string | Id<'_storage'> | null | undefined,
  options?: {
    /**
     * @deprecated No-op. The hook is now resolver-always for every storage key,
     * so forcing the resolver is the default and this flag has no effect. Kept
     * only for source compatibility with existing call sites; safe to remove.
     */
    forceResolver?: boolean;
    /**
     * Owning branding document for legacy Convex `_storage` media. The backend
     * verifies both organization membership and that the id belongs to this
     * exact branding before returning a URL.
     */
    brandingId?: string;
  }
): StorageUrlState {
  // `forceResolver` is accepted for backwards compatibility but no longer does
  // anything: the resolver is always used for storage keys (see JSDoc).
  void options?.forceResolver;
  const { isAuthenticated } = useConvexAuth();
  const refreshStorageUrl = useMutation(api.storage.resolver.refreshStorageUrl);
  const readScope = useMessageReadScope();
  // Anonymous share viewers (publicLink) cannot use the authenticated
  // resolver; they resolve through the token-gated share query below.
  // AUTHENTICATED grantees keep the normal resolver path first — the storage
  // resolver's thread-share branch (canAccessStorageKey) authorizes user/email
  // grantees. But publicLink grants never match a signed-in principal, so a
  // signed-in non-member on a public share link gets a resolver DENY
  // (url: null, refreshRequired: false) — that case falls back to the
  // token-gated query below instead of rendering blank media.
  const isAnonymousShareScope = readScope.mode === 'share' && !isAuthenticated;
  const shareToken = readScope.mode === 'share' ? readScope.token : null;

  const isAlreadyUrl =
    typeof storageIdOrUrl === 'string' &&
    (storageIdOrUrl.startsWith('http') || storageIdOrUrl.startsWith('data:'));

  const isStorageReference =
    !!storageIdOrUrl && !isAlreadyUrl && typeof storageIdOrUrl === 'string';

  // Legacy Convex `_storage` ids have no '/'; R2 storage keys always do
  // (`org/mediaType/uuid.ext`). Same heuristic `getStorageUrl` uses server-side.
  const isLegacyStorageId =
    isStorageReference && !(storageIdOrUrl as string).includes('/');

  const cacheKey =
    isStorageReference && !isLegacyStorageId
      ? (storageIdOrUrl as string)
      : null;
  const cachedEntry = cacheKey ? storageUrlCache.get(cacheKey) : undefined;

  // Query the narrow resolver for every storage-key reference (never derived on
  // the client) whose cached entry is missing or stale.
  const shouldQuery =
    isStorageReference &&
    !isLegacyStorageId &&
    isAuthenticated &&
    !cachedEntryIsFresh(cachedEntry) &&
    !!storageIdOrUrl;

  const { data } = useQuery({
    ...convexQuery(
      api.storage.resolver.resolveStorageUrl,
      shouldQuery && storageIdOrUrl
        ? { storageKey: String(storageIdOrUrl) }
        : 'skip'
    ),
    enabled: shouldQuery,
  });

  // Share-scope token-gated resolution: one query per storage key. Used for
  // anonymous viewers always, and for signed-in viewers only after the
  // authenticated resolver DENIED the key (publicLink grants never match a
  // signed-in principal). The query returns a public URL when the object is
  // already addressable (or an active public copy exists), or
  // `publicCopyPending` + workspaceFileId — in which case we fire the
  // idempotent copy trigger and let Convex reactivity deliver the URL when
  // the copy activates.
  const authedResolverDenied =
    readScope.mode === 'share' &&
    isAuthenticated &&
    data !== undefined &&
    data.url === null &&
    !data.refreshRequired;
  const shareCacheKey =
    (isAnonymousShareScope || authedResolverDenied) && cacheKey && shareToken
      ? `${shareToken}|${cacheKey}`
      : null;
  const cachedShareUrl = shareCacheKey
    ? anonymousShareUrlCache.get(shareCacheKey)
    : undefined;
  const shouldQueryShare =
    shareCacheKey !== null && typeof cachedShareUrl !== 'string';

  const { data: shareData } = useQuery({
    ...convexQuery(
      api.organizations.threads.shared_access.resolveSharedThreadMediaUrls,
      shouldQueryShare && shareToken && cacheKey
        ? {
            token: shareToken,
            refs: [{ kind: 'storageKey' as const, storageKey: cacheKey }],
          }
        : 'skip'
    ),
    enabled: shouldQueryShare,
  });
  const shareResult = shareData?.[0];

  useEffect(() => {
    if (shareCacheKey && shareResult?.url) {
      anonymousShareUrlCache.set(shareCacheKey, shareResult.url);
    }
  }, [shareCacheKey, shareResult?.url]);

  const ensureSharedThreadFilePublicCopy = useAction(
    api.organizations.threads.thread_share_public_copies_actions
      .ensureSharedThreadFilePublicCopy
  );
  // On a failed trigger (transient network/server error) none of the effect's
  // other deps change — `publicCopyPending` stays true — so bump a retry tick
  // after a delay or the viewer would be stuck with blank media until reload.
  const [copyRetryTick, setCopyRetryTick] = useState(0);
  const copyRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (copyRetryTimerRef.current !== null) {
        clearTimeout(copyRetryTimerRef.current);
        copyRetryTimerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    const pendingFileId = shareResult?.publicCopyPending
      ? shareResult.workspaceFileId
      : undefined;
    if (!pendingFileId || !shareToken) return;
    const triggerKey = String(pendingFileId);
    if (pendingPublicCopyTriggers.has(triggerKey)) return;
    pendingPublicCopyTriggers.add(triggerKey);
    void ensureSharedThreadFilePublicCopy({
      token: shareToken,
      workspaceFileId: pendingFileId,
    })
      .catch(() => {
        if (copyRetryTimerRef.current === null) {
          copyRetryTimerRef.current = setTimeout(() => {
            copyRetryTimerRef.current = null;
            setCopyRetryTick((tick) => tick + 1);
          }, REFRESH_RETRY_DELAY_MS);
        }
      })
      .finally(() => {
        pendingPublicCopyTriggers.delete(triggerKey);
      });
  }, [
    ensureSharedThreadFilePublicCopy,
    shareResult?.publicCopyPending,
    shareResult?.workspaceFileId,
    shareToken,
    copyRetryTick,
  ]);

  // Legacy `_storage` id path: preserve the historical behavior exactly,
  // including caching null results to avoid duplicate queries.
  // KNOWN GAP: this leg is authenticated-only — there is no token-gated
  // share-scope resolver for legacy Convex storage ids (no index maps a bare
  // `_storage` id back to its owning thread), so pre-R2 media in a publicly
  // shared thread renders blank for anonymous viewers.
  const legacyCacheKey = isLegacyStorageId
    ? `${options?.brandingId ?? 'generic'}|${storageIdOrUrl as string}`
    : null;
  const cachedLegacyUrl = legacyCacheKey
    ? legacyStorageIdUrlCache.get(legacyCacheKey)
    : undefined;
  const shouldQueryLegacy =
    isLegacyStorageId && isAuthenticated && cachedLegacyUrl === undefined;

  const { data: legacyData } = useQuery({
    ...convexQuery(
      api.assets.queries.getStorageUrl,
      shouldQueryLegacy && storageIdOrUrl
        ? {
            storageId: storageIdOrUrl,
            ...(options?.brandingId ? { brandingId: options.brandingId } : {}),
          }
        : 'skip'
    ),
    enabled: shouldQueryLegacy,
  });

  useEffect(() => {
    if (legacyCacheKey && legacyData !== undefined) {
      legacyStorageIdUrlCache.set(legacyCacheKey, legacyData);
    }
  }, [legacyCacheKey, legacyData]);

  // Update cache when a resolver result arrives.
  useEffect(() => {
    if (cacheKey && data) {
      storageUrlCache.set(cacheKey, {
        url: data.url,
        urlExpiresAt: data.urlExpiresAt,
      });
    }
  }, [cacheKey, data]);

  // Refresh scheduling: when the resolver signals refreshRequired (private URL
  // missing/near-expiry), call the idempotent refresh mutation. The module-level
  // `refreshingKeys` set dedupes concurrent calls across all hook instances.
  // On failure, bump `retryTick` after a delay so the effect re-runs even though
  // its other dependencies (refreshRequired stays true) don't change.
  const [retryTick, setRetryTick] = useState(0);
  // The freshly-signed entry, mirrored into React state. The cache is a
  // module-level map, so writing to it is invisible to React on its own and a
  // long-mounted component would keep rendering the expired URL it already had.
  // Tagged with the key it belongs to: this hook's `storageIdOrUrl` can change
  // without remounting, and a refresh that lands after that must not hand one
  // key's signed URL to another.
  const [refreshedFor, setRefreshedFor] = useState<{
    key: string;
    entry: CachedEntry;
  } | null>(null);
  const refreshedEntry =
    refreshedFor && cacheKey && refreshedFor.key === cacheKey
      ? refreshedFor.entry
      : null;
  // Adopting the SAME entry twice must not re-render. `observed` hands back the
  // exact object already in the module cache, so a bare `setRefreshedFor({...})`
  // would allocate a new wrapper every time and spin any effect that re-runs on
  // render identity.
  const adoptEntry = useCallback((key: string, entry: CachedEntry) => {
    setRefreshedFor((prev) =>
      prev && prev.key === key && prev.entry === entry ? prev : { key, entry }
    );
  }, []);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `useMutation` is not contractually identity-stable across renders, and this
  // hook's refresh loop must not be: an unstable `doRefresh` re-runs the effects
  // below on every render, and each re-run used to cancel the in-flight result
  // that was going to re-arm the timer — the loop simply stopped. Read the
  // mutation through a ref so `doRefresh` is created once.
  const refreshStorageUrlRef = useRef(refreshStorageUrl);
  useEffect(() => {
    refreshStorageUrlRef.current = refreshStorageUrl;
  }, [refreshStorageUrl]);
  // Cancellation is by UNMOUNT, not by effect re-run — see above.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const doRefresh = useCallback(
    async (key: string): Promise<RefreshOutcome> => {
      // Freshness is checked BEFORE the in-flight guard on purpose. A component
      // that arrives just after another one finished refreshing this key must
      // adopt the winner's entry — returning "nothing happened" would leave it
      // rendering its own expired URL and arming its next timer against a
      // dead expiry.
      const cached = storageUrlCache.get(key);
      if (cached && cachedEntryIsFresh(cached)) {
        return { kind: 'observed', entry: cached };
      }
      // Another component holds the slot for this key. Don't pile a second
      // mutation onto it; report `deferred` so the caller re-arms and picks up
      // the winner's result on the next tick.
      if (refreshingKeys.has(key)) return { kind: 'deferred' };
      refreshingKeys.add(key);
      try {
        const result = await refreshStorageUrlRef.current({ storageKey: key });
        const entry: CachedEntry = {
          url: result.url,
          urlExpiresAt: result.urlExpiresAt,
        };
        storageUrlCache.set(key, entry);
        return { kind: 'refreshed', entry };
      } catch {
        // Leave cache as-is and schedule a retry for transient failures.
        if (retryTimerRef.current === null) {
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            setRetryTick((tick) => tick + 1);
          }, REFRESH_RETRY_DELAY_MS);
        }
        return { kind: 'failed' };
      } finally {
        refreshingKeys.delete(key);
      }
    },
    []
  );

  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!cacheKey || !data?.refreshRequired || !isAuthenticated) return;
    void doRefresh(cacheKey).then((outcome) => {
      if (!mountedRef.current) return;
      // `observed` counts: another instance already minted the URL this render
      // was waiting for.
      if (outcome.kind === 'refreshed' || outcome.kind === 'observed') {
        adoptEntry(cacheKey, outcome.entry);
      }
    });
  }, [
    cacheKey,
    data?.refreshRequired,
    isAuthenticated,
    doRefresh,
    adoptEntry,
    retryTick,
  ]);

  // Proactive refresh for long-mounted components.
  //
  // A private URL expires on a wall clock, but the checks above only run when
  // something re-renders. A gallery, a viewer, or a chat transcript left open
  // past the TTL therefore kept rendering an expired URL until an unrelated
  // render happened to re-evaluate freshness — the image simply broke. This
  // schedules the refresh against the URL's own expiry instead, so a mounted
  // component always holds a live URL; unmounting clears the timer, and the
  // module-level `refreshingKeys` set still collapses duplicate refreshes
  // across every component showing the same key.
  //
  // Re-arming does NOT depend on the expiry moving. `schedule.tick` is bumped
  // every time an attempt settles, so the effect re-runs — and arms the next
  // timer — even when the attempt produced no new expiry at all (a deferred
  // attempt behind another component's in-flight refresh, a transient failure,
  // or a server that handed the same URL back). Keying the loop on
  // `currentExpiresAt` alone is exactly how a mounted component used to fall
  // off the timer and go on rendering a URL that then expired.
  const currentExpiresAt = cacheKey
    ? (refreshedEntry?.urlExpiresAt ??
      storageUrlCache.get(cacheKey)?.urlExpiresAt ??
      data?.urlExpiresAt ??
      null)
    : null;
  const [proactiveSchedule, setProactiveSchedule] = useState({
    tick: 0,
    minDelayMs: MIN_PROACTIVE_REFRESH_DELAY_MS,
  });
  useEffect(() => {
    if (!cacheKey || !isAuthenticated || currentExpiresAt == null) return;
    const armedAgainst = currentExpiresAt;
    const delay = Math.max(
      proactiveSchedule.minDelayMs,
      armedAgainst - Date.now() - CLIENT_REFRESH_THRESHOLD_MS
    );
    const timer = setTimeout(() => {
      void doRefresh(cacheKey).then((outcome) => {
        // Guarded by mount, not by this effect run: a re-render that re-runs
        // the effect must not discard the very result that re-arms the loop.
        if (!mountedRef.current) return;
        if (outcome.kind === 'refreshed' || outcome.kind === 'observed') {
          adoptEntry(cacheKey, outcome.entry);
        }
        const advanced =
          (outcome.kind === 'refreshed' || outcome.kind === 'observed') &&
          outcome.entry.urlExpiresAt != null &&
          outcome.entry.urlExpiresAt > armedAgainst;
        setProactiveSchedule((prev) => ({
          tick: prev.tick + 1,
          minDelayMs: advanced
            ? MIN_PROACTIVE_REFRESH_DELAY_MS
            : outcome.kind === 'refreshed'
              ? // The server had nothing newer to give. Re-arm, but slowly:
                // polling it at the 5s floor would be a stampede of its own.
                NON_ADVANCING_REFRESH_BACKOFF_MS
              : // Deferred behind another component, or a failed attempt whose
                // own retry timer is already running: come back promptly.
                MIN_PROACTIVE_REFRESH_DELAY_MS,
        }));
      });
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [
    cacheKey,
    currentExpiresAt,
    isAuthenticated,
    doRefresh,
    adoptEntry,
    proactiveSchedule,
  ]);

  const resolvedUrl = useMemo(() => {
    if (isAlreadyUrl && typeof storageIdOrUrl === 'string') {
      return storageIdOrUrl;
    }
    if (shareCacheKey) {
      if (typeof cachedShareUrl === 'string') return cachedShareUrl;
      return shareResult?.url ?? null;
    }
    if (legacyCacheKey) {
      if (cachedLegacyUrl !== undefined) {
        return cachedLegacyUrl;
      }
      return legacyData ?? null;
    }
    if (cacheKey) {
      if (refreshedEntry?.url) return refreshedEntry.url;
      const cached = storageUrlCache.get(cacheKey);
      if (cached && cached.url) {
        return cached.url;
      }
    }
    if (data && data.url) {
      return data.url;
    }
    return null;
  }, [
    cacheKey,
    cachedLegacyUrl,
    cachedShareUrl,
    data,
    isAlreadyUrl,
    legacyCacheKey,
    legacyData,
    // A completed refresh writes to the module-level cache, which this memo
    // reads; mirroring the entry into state is what makes it observable here.
    refreshedEntry,
    shareCacheKey,
    shareResult?.url,
    storageIdOrUrl,
  ]);

  const isLoading = Boolean(
    !resolvedUrl &&
      storageIdOrUrl &&
      ((shareCacheKey !== null &&
        ((shouldQueryShare && shareData === undefined) ||
          shareResult?.publicCopyPending === true)) ||
        (legacyCacheKey !== null &&
          isAuthenticated &&
          cachedLegacyUrl === undefined &&
          legacyData === undefined) ||
        (cacheKey !== null &&
          isAuthenticated &&
          (data === undefined || data.refreshRequired)))
  );

  return {
    url: resolvedUrl,
    status: resolvedUrl ? 'ready' : isLoading ? 'loading' : 'unavailable',
  };
}

export function useStorageUrl(
  storageIdOrUrl: string | Id<'_storage'> | null | undefined,
  options?: {
    forceResolver?: boolean;
    brandingId?: string;
  }
): string | null {
  return useStorageUrlState(storageIdOrUrl, options).url;
}

/**
 * Hook to get the best available product image URL
 * Tries main asset first, then converted original image URL
 */
export function useProductImageUrl(
  product: { originalImageUrl?: string | null } | null | undefined,
  assetsData:
    | { assets?: { main?: Array<{ url?: string | null }> } }
    | null
    | undefined
): string | null {
  const originalImageUrl = useStorageUrl(product?.originalImageUrl);

  // Get main asset if available
  const mainAsset = assetsData?.assets?.main?.[0];

  return mainAsset?.url || originalImageUrl || null;
}
